import { useState } from 'react';
import { DIFF_TYPE } from '@ardoq/global-consts';
import {
  DiffType,
  type EnhancedScopeData,
  type ScopeSize,
} from '@ardoq/data-model';
import {
  type APIDiffBranchToBranch,
  type APIModelAttributes,
  type APIScenarioScopeData,
  type APIScopeData,
  type ArdoqId,
  type Entity,
  PersonalSetting,
  Verb,
} from '@ardoq/api-types';
import {
  type PermissionContext,
  permissionsOperations,
} from '@ardoq/access-control';
import { alert, confirm } from '@ardoq/modal';
import { Icon, IconName } from '@ardoq/icons';
import styled from 'styled-components';
import { getVerbFromMergeStep } from 'components/DiffMergeSidebarNavigator/utils';
import { getWindowLocationPath, setWindowLocation } from 'router/routerUtils';
import { type MergeStep } from 'components/DiffMergeTable/types';
import { getActiveScenarioId } from 'streams/activeScenario/activeScenario$';
import { dispatchAction } from '@ardoq/rxbeach';
import { updatePersonalSetting } from 'streams/currentUser/actions';
import { setScenarioIsInSync } from 'scope/actions';
import { resetReferences } from 'streams/references/ReferenceActions';
import { cloneDeep } from 'lodash';
import { initMerge } from './merge/actions';
import { MergeDirection } from './merge/MergeDirection';
import { isArdoqError, toByIdDictionary } from '@ardoq/common-helpers';
import { scenarioApi } from '@ardoq/api';
import { toId } from '@ardoq/core';
import { Checkbox } from '@ardoq/forms';
import { logError } from '@ardoq/logging';
import { t } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';

export const clearGraph = () => {
  dispatchAction(resetReferences());
};

const setDiffType = <APIAttributeType,>(
  attrs: APIAttributeType,
  diffType: DiffType
) => ({
  ...attrs,
  [DIFF_TYPE]: diffType,
});

const addMissingFields = <APIAttributeType,>(
  attrs: APIAttributeType,
  diff?: Partial<APIAttributeType>
): APIAttributeType => {
  if (!diff) {
    return attrs;
  }
  const missingFields = Object.keys(diff).reduce(
    (accumulatedMissingFields, key) =>
      Object.hasOwnProperty.call(attrs, key)
        ? accumulatedMissingFields
        : { ...accumulatedMissingFields, [key]: null },
    {}
  );
  return { ...attrs, ...missingFields };
};

const getDiffType = (
  diffDataCollection: APIDiffBranchToBranch[keyof APIDiffBranchToBranch],
  id: ArdoqId
) => {
  if (diffDataCollection.create[id]) {
    return DiffType.ADDED;
  }
  if (diffDataCollection.delete[id]) {
    return DiffType.REMOVED;
  }

  if (diffDataCollection.update?.[id]) {
    return DiffType.CHANGED;
  }
  return DiffType.UNCHANGED;
};

export const getEntities = <APIAttributeType extends { _id: ArdoqId }>(
  branchCollection: APIAttributeType[],
  masterCollection: APIAttributeType[],
  diffDataCollection: APIDiffBranchToBranch[keyof APIDiffBranchToBranch]
) => {
  const update =
    (diffDataCollection.update as Record<ArdoqId, Partial<APIAttributeType>>) ||
    {};

  const masterCollectionById = toByIdDictionary(masterCollection);
  const branchCollectionById = toByIdDictionary(branchCollection);

  return {
    // TODO? can be optimezed by only providing the list of entities which
    // have to be changed.
    branch: [
      ...branchCollection.map(attrs =>
        setDiffType<APIAttributeType>(
          addMissingFields(attrs, update[attrs._id]),
          DiffType.NONE
        )
      ),
      ...createCopyAndSetDiffType(
        diffDataCollection.delete,
        masterCollectionById,
        DiffType.PLACEHOLDER
      ),
    ],
    diff: [
      ...branchCollection.map(attrs =>
        setDiffType<APIAttributeType>(
          addMissingFields(attrs, update[attrs._id]),
          getDiffType(diffDataCollection, attrs._id)
        )
      ),
      ...createCopyAndSetDiffType(
        diffDataCollection.delete,
        masterCollectionById,
        DiffType.REMOVED
      ),
    ],
    master: [
      ...masterCollection.map(attrs =>
        setDiffType<APIAttributeType>(
          addMissingFields(attrs, update[attrs._id]),
          DiffType.NONE
        )
      ),
      ...createCopyAndSetDiffType(
        diffDataCollection.create,
        branchCollectionById,
        DiffType.PLACEHOLDER
      ),
    ],
  };
};

// The entity attributes in diffDataCollection are only
// a subset based on ignoredProperties and the dynamicFilter
// of the original attribute. Cloning them from the
// source to provide the complete attributes for the backbone models.
const createCopyAndSetDiffType = <APIAttributeType extends { _id: ArdoqId }>(
  dataCollectionDict: APIDiffBranchToBranch[keyof APIDiffBranchToBranch][
    | Verb.DELETE
    | Verb.CREATE],
  collectionById: Record<ArdoqId, APIAttributeType>,
  diffType: DiffType
) =>
  Object.keys(dataCollectionDict).map(id =>
    setDiffType<APIAttributeType>(cloneDeep(collectionById[id]), diffType)
  );

export const getOnlyScenarioRelatedIds = (
  models: Entity[],
  connectedModels: Entity[]
): ArdoqId[] => {
  const scenarioContextIds = new Set(models.map(toId));
  return connectedModels.map(toId).filter(id => !scenarioContextIds.has(id));
};

const Block = styled.div`
  margin-bottom: 25px;
  display: flex;
  align-items: center;
`;

export const openScenarioIsOutOfSyncModal = async () => {
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders confirm() body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;
  const isSyncNow = await confirm({
    title: t(
      i18nLocale,
      'scope:outOfSyncModal.title',
      'Incompatible metamodels'
    ),
    text: (
      <>
        <Block>
          {t(
            i18nLocale,
            'scope:outOfSyncModal.body',
            'The related components section and visual diff in scenarios are not available when the metamodel in the mainline has changes that are incompatible with the metamodel in the scenario. To get the metamodels back to a compatible state, please use the scenario merge functionality.'
          )}
        </Block>
      </>
    ),
    confirmButtonTitle: t(
      i18nLocale,
      'scope:outOfSyncModal.confirmButton',
      'Sync metamodels now'
    ),
    cancelButtonTitle: t(
      i18nLocale,
      'scope:outOfSyncModal.cancelButton',
      'Cancel'
    ),
    confirmButtonClickId: 'incompatible-metamodel-scenario-sync-now',
    cancelButtonClickId: 'incompatible-metamodel-scenario-close',
  });
  if (isSyncNow) {
    dispatchAction(initMerge(MergeDirection.MAINLINE_TO_BRANCH));
  }
  return isSyncNow;
};

const WelcomeScenarioMessage = () => {
  // i18nLocale$.state OK here: rendered inside an imperative alert() opener
  const i18nLocale = i18nLocale$.state;
  const [showAgain, setShowAgain] = useState(false);
  return (
    <>
      <Block>
        {t(
          i18nLocale,
          'scope:welcomeScenarioModal.body1',
          'Not all views have the capability of visualizing the entire scenario, related components and the difference between mainline and the scenario.'
        )}
      </Block>
      <Block>
        {t(
          i18nLocale,
          'scope:welcomeScenarioModal.body2',
          'The views that do support this are marked with the Scenario icon'
        )}
        <Icon style={{ marginLeft: 10 }} iconName={IconName.SCENARIO} />
      </Block>
      <Checkbox
        isChecked={showAgain}
        onChange={() => {
          setShowAgain(!showAgain);
          dispatchAction(
            updatePersonalSetting({
              personalSetting: PersonalSetting.SCENARIOS_HIDE_WELCOME_MODAL,
              newValue: !showAgain,
            })
          );
        }}
      >
        {t(
          i18nLocale,
          'scope:welcomeScenarioModal.dontShowAgain',
          "Don't show me this message again"
        )}
      </Checkbox>
    </>
  );
};

export const openWelcomeScenarioModal = () => {
  // i18nLocale$.state OK here: imperative alert() opener
  const i18nLocale = i18nLocale$.state;
  return alert({
    title: t(i18nLocale, 'scope:welcomeScenarioModal.title', 'Scenario views'),
    text: <WelcomeScenarioMessage />,
    confirmButtonTitle: t(
      i18nLocale,
      'scope:welcomeScenarioModal.closeButton',
      'Close'
    ),
  });
};

const getChildrenCount = <T extends { children: Record<string, T> }>(
  obj: Record<string, T>
): number =>
  Object.values(obj).reduce(
    (acc, value) => acc + 1 + getChildrenCount(value.children),
    0
  );

export const getScopeSize = (
  scopeData: APIScopeData | EnhancedScopeData
): ScopeSize => {
  const { referenceTypesCount, componentTypesCount } = (
    scopeData.models as APIModelAttributes[]
  ).reduce(
    (acc, model) => ({
      referenceTypesCount:
        acc.referenceTypesCount + Object.values(model.referenceTypes).length,
      componentTypesCount:
        acc.componentTypesCount + getChildrenCount(model.root),
    }),
    {
      referenceTypesCount: 0,
      componentTypesCount: 0,
    }
  );

  return {
    componentsCount: scopeData.components.length,
    referencesCount: scopeData.references.length,
    workspacesCount: scopeData.workspaces.length,
    tagsCount: scopeData.tags.length,
    modelsCount: scopeData.models.length,
    fieldsCount: scopeData.fields.length,
    componentTypesCount,
    referenceTypesCount,
  };
};

const SEARCH_PARAM_KEY = 'mergeFlow';

export const setMergeStepInWindowLocation = (mergeStep?: MergeStep) => {
  const verb = mergeStep ? getVerbFromMergeStep(mergeStep) : ''; // for the 'no changes to merge' page just use an empty string
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set(SEARCH_PARAM_KEY, verb);
  setWindowLocation(
    `${getWindowLocationPath()}?${searchParams.toString()}`,
    'push',
    null
  );
};

export const removeMergeStepFromWindowLocation = () => {
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.delete(SEARCH_PARAM_KEY);
  setWindowLocation(
    `${getWindowLocationPath()}?${searchParams.toString()}`,
    'push',
    null
  );
};

export const updateSyncStatusIfChanged = async (scenarioId?: ArdoqId) => {
  const isInSync = await scenarioApi.isInSync(
    scenarioId ?? getActiveScenarioId()!
  );
  if (isArdoqError(isInSync)) {
    return logError(isInSync, 'Error checking if scenario is in sync');
  }
  if (isInSync) {
    dispatchAction(setScenarioIsInSync({ isInSync }));
  }
};

export const hasReadAccessToAllWorkspacesInScenario = (
  scenarioData: APIScenarioScopeData,
  permissionContext: PermissionContext
): boolean =>
  permissionsOperations.hasAdminRole(permissionContext) ||
  scenarioData.workspaces.every(ws =>
    permissionsOperations.hasResourceReadAccess(
      permissionContext.permissions,
      ws._id
    )
  );

export const getMissingWorkspacesInScenario = (
  scenarioData: APIScenarioScopeData,
  permissionContext: PermissionContext
) => {
  if (permissionsOperations.hasAdminRole(permissionContext)) return [];
  return scenarioData.workspaces.filter(
    ws =>
      !permissionsOperations.hasResourceReadAccess(
        permissionContext.permissions,
        ws._id
      )
  );
};
